import { z } from 'zod';
import { booleanish, csvEnum, cursorPageSchema, optionalBooleanish, severitySchema, } from './common.js';
export const incidentCategorySchema = z.enum([
    'FIBER_CUT',
    'POWER',
    'EQUIPMENT',
    'UPSTREAM',
    'CONGESTION',
    'CONFIGURATION',
    'WEATHER',
    'PLANNED',
    'OTHER',
]);
export const incidentStatusSchema = z.enum([
    'OPEN',
    'INVESTIGATING',
    'IDENTIFIED',
    'MONITORING',
    'RESOLVED',
    'CLOSED',
]);
export const incidentCreateSchema = z.object({
    title: z.string().min(3).max(200),
    description: z.string().max(8000).optional(),
    severity: severitySchema,
    category: incidentCategorySchema,
    rootElementId: z.string().uuid().optional(),
    assignedTo: z.string().uuid().optional(),
    detectedAt: z.coerce.date().optional(),
    alarmIds: z.array(z.string().uuid()).max(1000).optional(),
});
export const incidentUpdateSchema = z.object({
    title: z.string().min(3).max(200).optional(),
    description: z.string().max(8000).optional(),
    severity: severitySchema.optional(),
    status: incidentStatusSchema.optional(),
    category: incidentCategorySchema.optional(),
    assignedTo: z.string().uuid().nullable().optional(),
});
export const incidentFilterSchema = cursorPageSchema.extend({
    status: csvEnum(['OPEN', 'INVESTIGATING', 'IDENTIFIED', 'MONITORING', 'RESOLVED', 'CLOSED']).optional(),
    severity: csvEnum(['P1', 'P2', 'P3', 'P4']).optional(),
    category: z.string().optional(),
    assignedTo: z.string().uuid().optional(),
    /** Convenience for the NOC's default view. */
    openOnly: booleanish(false),
    slaBreached: optionalBooleanish(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    search: z.string().max(160).optional(),
});
export const incidentResolveSchema = z.object({
    rootCause: z.string().min(1).max(4000),
    resolutionNotes: z.string().min(1).max(8000),
    /**
     * Category can change at resolution. It usually should — the initial
     * category is a guess made by the correlator in the first seconds, and the
     * trend analytics are only as good as the corrected value.
     */
    category: incidentCategorySchema.optional(),
});
export const incidentEscalateSchema = z.object({
    reason: z.string().min(1).max(1000),
    severity: severitySchema.optional(),
    notifyRoles: z.array(z.string()).optional(),
});
export const incidentMergeSchema = z.object({
    targetId: z.string().uuid(),
    reason: z.string().max(1000).optional(),
});
export const incidentImpactSchema = z.object({
    computedAt: z.coerce.date(),
    subscribersTotal: z.number().int(),
    subscribersBusiness: z.number().int(),
    subscribersResidential: z.number().int(),
    /**
     * Surfaced in the API, not hidden. If 200 subscribers below this element
     * have no ONU mapping, the affected count is an undercount and the NOC
     * needs to know that before quoting it.
     */
    subscribersUnmapped: z.number().int(),
    mrrExposed: z.string(),
    currency: z.string(),
    barangays: z.array(z.object({ name: z.string(), count: z.number().int() })),
    estimatedRestoreMin: z.number().int().nullable(),
});
export const incidentSummarySchema = z.object({
    id: z.string().uuid(),
    ref: z.string(),
    title: z.string(),
    severity: severitySchema,
    status: incidentStatusSchema,
    category: incidentCategorySchema,
    detectedAt: z.coerce.date(),
    acknowledgedAt: z.coerce.date().nullable(),
    resolvedAt: z.coerce.date().nullable(),
    slaDueAt: z.coerce.date().nullable(),
    slaBreached: z.boolean(),
    assignee: z.object({ id: z.string().uuid(), fullName: z.string() }).nullable(),
    rootElement: z.object({ id: z.string().uuid(), label: z.string(), kind: z.string() }).nullable(),
    alarmCount: z.number().int(),
    impact: incidentImpactSchema.nullable(),
});
export const postIncidentReviewSchema = z.object({
    summary: z.string().min(1).max(8000),
    timelineReview: z.string().max(8000).optional(),
    whatWentWell: z.string().max(4000).optional(),
    whatWentPoorly: z.string().max(4000).optional(),
    contributingFactors: z.string().max(4000).optional(),
    actionItems: z
        .array(z.object({
        description: z.string().min(1).max(500),
        owner: z.string().max(120).optional(),
        dueOn: z.coerce.date().optional(),
    }))
        .default([]),
});
//# sourceMappingURL=incidents.js.map